import { useState } from "react";
import { RenderJob } from "@/lib/user-types";
import { RenderCard } from "@/components/render-card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface RenderJobsListProps {
  renderJobs: RenderJob[];
  isLoading: boolean;
  itemsPerPage?: number;
  emptyMessage?: string;
}

export function RenderJobsList({
  renderJobs,
  isLoading,
  itemsPerPage = 5,
  emptyMessage = "No render jobs found."
}: RenderJobsListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(renderJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRenderJobs = renderJobs.slice(startIndex, endIndex);
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (renderJobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }
  
  return (
    <div>
      <ul className="divide-y divide-gray-200">
        {currentRenderJobs.map(renderJob => (
          <RenderCard key={renderJob.id} renderJob={renderJob} />
        ))}
      </ul>
      
      <div className="flex items-center justify-between mt-4 px-2">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(endIndex, renderJobs.length)}
            </span>{" "}
            of <span className="font-medium">{renderJobs.length}</span> results
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {totalPages > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous</span>
            </Button>
          )}
          
          <span className="text-sm font-medium">
            Page {currentPage} {totalPages > 1 ? `of ${totalPages}` : ''}
          </span>
          
          {totalPages > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
